import { useState } from 'react';
import { FileText, Image } from 'lucide-react';
import {
  Button,
  DataTable,
  ErrorState,
  FilterBar,
  LoadingState,
  Modal,
  Pager,
  SelectableChip,
  StatusPill,
  type DataTableColumn,
} from '../../components';
import { formatDate, formatDateTime } from '../../lib/format/dates';
import { formatCount } from '../../lib/format/money';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { humanize, pickParam, stringParam } from '../profiles/people';
import { CONTENT_TYPES, assetFileUrl, assetTitle, type ContentAsset, type ContentType } from './api';
import { useContentDownloads, useContentLibrary, useTrackDownload } from './queries';

const TYPE_LABEL: Record<ContentType, string> = {
  social_share_template: 'Social template',
  ambassador_material: 'Ambassador material',
};

function columns(onDownload: (a: ContentAsset) => void, busyId: string | null): DataTableColumn<ContentAsset>[] {
  return [
    {
      id: 'name',
      header: 'Asset',
      width: '2.6fr',
      wrap: true,
      cell: (a) => {
        const thumb = a.type === 'social_share_template' ? a.data[0]?.imageUrl : undefined;
        return (
          <span className="inline-flex min-w-0 items-center gap-[11px]">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-cream-warm text-brown">
              {thumb ? (
                <img src={thumb} alt="" className="h-full w-full object-cover" />
              ) : a.type === 'social_share_template' ? (
                <Image size={17} aria-hidden="true" />
              ) : (
                <FileText size={17} aria-hidden="true" />
              )}
            </span>
            <span className="min-w-0">
              <span className="block truncate font-semibold">{assetTitle(a)}</span>
              <span className="block font-mono text-xs text-muted">
                {a.category}
                {a.lang && a.lang !== 'en' ? ` · ${a.lang}` : ''}
              </span>
            </span>
          </span>
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      width: '176px',
      cell: (a) => (
        <StatusPill tone={a.type === 'social_share_template' ? 'info' : 'chef'}>{TYPE_LABEL[a.type]}</StatusPill>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      width: '1fr',
      cell: (a) => <span className="text-muted">{humanize(a.category)}</span>,
    },
    {
      id: 'downloads',
      header: 'Downloads',
      width: '96px',
      align: 'right',
      numeric: true,
      cell: (a) => formatCount(a.downloads),
    },
    {
      id: 'people',
      header: 'People',
      width: '76px',
      align: 'right',
      numeric: true,
      cell: (a) => formatCount(a.distinctDownloaders),
    },
    {
      id: 'updated',
      header: 'Updated',
      width: '1fr',
      cell: (a) => <span className="text-muted">{formatDate(a.updatedAt)}</span>,
    },
    {
      id: 'dl',
      header: '',
      width: '120px',
      align: 'right',
      action: true,
      cell: (a) =>
        assetFileUrl(a) ? (
          <Button
            variant="ghost-dark"
            size="sm"
            loading={busyId === a.id}
            onClick={(e) => {
              e.stopPropagation();
              onDownload(a);
            }}
          >
            Download
          </Button>
        ) : null,
    },
  ];
}

/**
 * Content Library (kit `ContentLibraryScreen`; FE plan §7). The admin app
 * distributes content; it does not create it — assets are added by a DevOps
 * deploy, so there is no upload here (B8). Downloading logs who took what;
 * a row opens its download history (`?asset=`).
 */
export function ContentScreen() {
  const table = useTableUrlState();
  const type = pickParam(table.filters.type, CONTENT_TYPES);
  const assetId = stringParam(table.filters.asset);
  const q = useContentLibrary({ page: table.page, pageSize: table.pageSize, type, searchTerm: table.q || undefined });
  const track = useTrackDownload();
  const [busyId, setBusyId] = useState<string | null>(null);
  const openAsset = q.data?.items.find((a) => a.id === assetId);

  const download = (a: ContentAsset) => {
    const url = assetFileUrl(a);
    if (!url) return;
    // Opened first, inside the click, so the browser does not block it; the
    // download is logged alongside.
    window.open(url, '_blank', 'noopener');
    setBusyId(a.id);
    track.mutate(a.id, { onSettled: () => setBusyId(null) });
  };

  return (
    <div>
      <FilterBar>
        <SelectableChip active={!type} onClick={() => table.setFilter('type', undefined)}>
          All
        </SelectableChip>
        {CONTENT_TYPES.map((t) => (
          <SelectableChip key={t} active={type === t} onClick={() => table.setFilter('type', t)}>
            {TYPE_LABEL[t]}
          </SelectableChip>
        ))}
      </FilterBar>
      <DataTable
        label="Content library"
        minWidth={1000}
        columns={columns(download, busyId)}
        rows={q.data?.items ?? []}
        getRowId={(a) => a.id}
        onRowClick={(a) => table.setParams({ asset: a.id })}
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        emptyLabel="No content yet. Assets are added by a deploy."
      />
      <Pager
        page={table.page}
        pageSize={table.pageSize}
        total={q.data?.total ?? 0}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        noun="assets"
      />
      {assetId && (
        <DownloadsDialog
          assetId={assetId}
          title={openAsset ? assetTitle(openAsset) : 'Downloads'}
          onClose={() => table.setParams({ asset: undefined })}
        />
      )}
    </div>
  );
}

function DownloadsDialog({ assetId, title, onClose }: { assetId: string; title: string; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const q = useContentDownloads(assetId, { page, pageSize: 25 });
  return (
    <Modal isOpen onClose={onClose} size="md" title={title}>
      {q.isPending ? (
        <LoadingState label="Loading downloads…" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <p className="mt-0 text-sm text-muted">
            {formatCount(q.data.total)} downloads by {formatCount(q.data.distinctDownloaders)} people.
          </p>
          {q.data.items.length === 0 ? (
            <div className="text-[13px] text-muted">Nobody has downloaded this yet.</div>
          ) : (
            q.data.items.map((d) => (
              <div
                key={d.id}
                className="flex items-center gap-3 border-t border-[rgba(143,68,32,.08)] py-2 text-[13px]"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-ink">{d.displayName || d.profileId}</span>
                  {d.email && <span className="block truncate text-muted">{d.email}</span>}
                </span>
                <span className="shrink-0 text-muted">{formatDateTime(d.downloadedAt)}</span>
              </div>
            ))
          )}
          {q.data.total > 25 && (
            <Pager page={page} pageSize={25} total={q.data.total} onPageChange={setPage} noun="downloads" />
          )}
        </>
      )}
    </Modal>
  );
}
